import type {
  PositionNode,
  RepertoireEdge,
  ReviewGrade,
  ReviewGradeCreate,
  ReviewItem,
  ReviewItemState,
  ReviewQueue,
  ReviewQueueMode,
  ReviewSummary,
} from '../api/types';
import { legalUciMoves } from './chess';

/**
 * Mock-only review scheduling for the §4 v1.7 review endpoints. It mirrors the
 * server's queue contract (own-side main/alternative edges, main-line-first walk,
 * arrival paths) so the trainer can be demoed and E2E-tested without a backend.
 * The intervals are a fixed toy table, not FSRS: only the server schedules.
 */

interface MockState {
  dueAt: string;
  reps: number;
  lapses: number;
  lastGrade: 'correct' | 'alternative' | 'incorrect';
  lastReviewAt: string;
  stability: number;
}

export interface MockReviewGraph {
  side: 'white' | 'black' | 'both';
  rootPositionId: string;
  edges: RepertoireEdge[];
  position: (positionId: string) => PositionNode | undefined;
}

const ROLE_ORDER: Record<string, number> = { main: 0, opponent: 1, alternative: 2, avoid: 3 };
const DAY = 86_400_000;
const states = new Map<string, MockState>();
let seeded = false;

function key(repertoireId: string, edgeId: string): string {
  return `${repertoireId}:${edgeId}`;
}

function isOwnMove(side: MockReviewGraph['side'], sideToMove: 'w' | 'b'): boolean {
  return side === 'both' || (side === 'white') === (sideToMove === 'w');
}

interface Walked {
  edge: RepertoireEdge;
  path: RepertoireEdge[];
}

function walk(graph: MockReviewGraph, scope?: string): { walked: Walked[]; start: string } {
  const enabled = graph.edges.filter((edge) => edge.enabled);
  const children = new Map<string, RepertoireEdge[]>();
  for (const edge of enabled) {
    const list = children.get(edge.transition.from.positionId) ?? [];
    list.push(edge);
    children.set(edge.transition.from.positionId, list);
  }
  for (const list of children.values()) {
    list.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || b.priority - a.priority);
  }
  const run = (start: string, prefix: RepertoireEdge[], visited: Set<string>): Walked[] => {
    const out: Walked[] = [];
    visited.add(start);
    const stack: Walked[] = [...(children.get(start) ?? [])].reverse().map((edge) => ({ edge, path: prefix }));
    while (stack.length > 0) {
      const next = stack.pop()!;
      out.push(next);
      const target = next.edge.transition.to.positionId;
      if (visited.has(target)) continue;
      visited.add(target);
      const path = [...next.path, next.edge];
      for (const child of [...(children.get(target) ?? [])].reverse()) stack.push({ edge: child, path });
    }
    return out;
  };
  if (scope === undefined) {
    return { walked: run(graph.rootPositionId, [], new Set()), start: graph.rootPositionId };
  }
  const full = run(graph.rootPositionId, [], new Set());
  const arrival = full.find((w) => w.edge.transition.to.positionId === scope);
  const prefix = arrival ? [...arrival.path, arrival.edge] : [];
  return {
    walked: run(scope, prefix, new Set()),
    start: scope === graph.rootPositionId || arrival ? graph.rootPositionId : scope,
  };
}

function items(graph: MockReviewGraph, scope?: string): { walked: Walked[]; start: string } {
  const { walked, start } = walk(graph, scope);
  return {
    start,
    walked: walked.filter((w) => {
      const source = graph.position(w.edge.transition.from.positionId);
      return (w.edge.role === 'main' || w.edge.role === 'alternative') &&
        source !== undefined && isOwnMove(graph.side, source.sideToMove);
    }),
  };
}

/** Give the demo repertoire a realistic mix: two due, one learned, one new. */
function seed(repertoireId: string, graph: MockReviewGraph): void {
  if (seeded) return;
  seeded = true;
  const { walked } = items(graph);
  const now = Date.now();
  walked.slice(0, 3).forEach((w, index) => {
    const dueAt = index === 2 ? now + 2 * DAY : now - (index + 1) * 3_600_000;
    states.set(key(repertoireId, w.edge.edgeId), {
      dueAt: new Date(dueAt).toISOString(),
      reps: 2,
      lapses: index === 1 ? 1 : 0,
      lastGrade: index === 1 ? 'incorrect' : 'correct',
      lastReviewAt: new Date(now - 3 * DAY).toISOString(),
      stability: 3,
    });
  });
}

export function seedMockReviews(repertoireId: string, graph: MockReviewGraph): void {
  seed(repertoireId, graph);
}

export function mockReviewSummary(
  repertoireId: string,
  graph: MockReviewGraph,
  scope?: string,
): ReviewSummary {
  const now = Date.now();
  const { walked } = items(graph, scope);
  const reviewed = walked
    .map((w) => states.get(key(repertoireId, w.edge.edgeId)))
    .filter((state): state is MockState => state !== undefined);
  const due = reviewed.filter((state) => Date.parse(state.dueAt) <= now).length;
  const upcoming = reviewed.map((state) => Date.parse(state.dueAt)).filter((at) => at > now);
  const today = new Date(new Date(now).toISOString().slice(0, 10)).getTime();
  const forecast = Array.from({ length: 7 }, (_, day) => ({
    date: new Date(today + day * DAY).toISOString().slice(0, 10),
    count: day === 0 ? due : 0,
  }));
  for (const at of upcoming) {
    const day = Math.floor((at - today) / DAY);
    if (day >= 0 && day < 7) forecast[day].count += 1;
  }
  return {
    totalCount: walked.length,
    newCount: walked.length - reviewed.length,
    dueCount: due,
    scheduledCount: reviewed.length - due,
    nextDueAt: upcoming.length ? new Date(Math.min(...upcoming)).toISOString() : null,
    forecast,
  };
}

export function mockReviewQueue(
  repertoireId: string,
  graph: MockReviewGraph,
  mode: ReviewQueueMode,
  limit: number,
  scope?: string,
): ReviewQueue {
  const now = Date.now();
  const { walked, start } = items(graph, scope);
  const stateOf = (w: Walked) => states.get(key(repertoireId, w.edge.edgeId));
  const selected = (mode === 'due'
    ? walked.filter((w) => { const s = stateOf(w); return s !== undefined && Date.parse(s.dueAt) <= now; })
    : mode === 'new' ? walked.filter((w) => stateOf(w) === undefined) : walked).slice(0, limit);
  const startFen = graph.position(start)?.canonicalFen ?? '';
  const reviewItems: ReviewItem[] = selected.map((w) => {
    const source = graph.position(w.edge.transition.from.positionId)!;
    const state = stateOf(w);
    return {
      edgeId: w.edge.edgeId,
      source: w.edge.transition.from,
      fen: source.canonicalFen,
      sideToMove: source.sideToMove,
      uci: w.edge.transition.uci,
      san: w.edge.transition.san,
      targetFen: graph.position(w.edge.transition.to.positionId)?.canonicalFen ?? '',
      role: w.edge.role === 'alternative' ? 'alternative' : 'main',
      comment: w.edge.comment ?? null,
      legalMoves: legalUciMoves(source.canonicalFen),
      startFen,
      path: w.path.map((step) => ({
        edgeId: step.edgeId,
        uci: step.transition.uci,
        san: step.transition.san,
        fen: graph.position(step.transition.to.positionId)?.canonicalFen ?? '',
      })),
      siblings: walked
        .filter((other) => other.edge.transition.from.positionId === w.edge.transition.from.positionId &&
          other.edge.edgeId !== w.edge.edgeId)
        .map((other) => ({
          edgeId: other.edge.edgeId,
          uci: other.edge.transition.uci,
          san: other.edge.transition.san,
          role: other.edge.role === 'alternative' ? 'alternative' : 'main',
        })),
      isNew: state === undefined,
      dueAt: state?.dueAt ?? null,
      reps: state?.reps ?? 0,
      lapses: state?.lapses ?? 0,
    };
  });
  return { mode, items: reviewItems, summary: mockReviewSummary(repertoireId, graph, scope) };
}

const TOY_INTERVAL_DAYS: Record<Exclude<ReviewGrade, 'skip'>, number> = {
  incorrect: 0.007, alternative: 1, correct: 3,
};

export function mockGradeReview(
  repertoireId: string,
  graph: MockReviewGraph,
  body: ReviewGradeCreate,
): ReviewItemState | 'not-found' | 'not-scheduled' {
  const edge = graph.edges.find((candidate) => candidate.edgeId === body.edgeId);
  if (!edge) return 'not-found';
  const source = graph.position(edge.transition.from.positionId);
  if (!edge.enabled || (edge.role !== 'main' && edge.role !== 'alternative') ||
      !source || !isOwnMove(graph.side, source.sideToMove)) {
    return 'not-scheduled';
  }
  const k = key(repertoireId, body.edgeId);
  const previous = states.get(k);
  if (body.grade === 'skip') {
    return previous
      ? { edgeId: body.edgeId, scheduled: true, ...previous }
      : { edgeId: body.edgeId, scheduled: false, reps: 0, lapses: 0 };
  }
  const now = Date.now();
  const growth = previous && body.grade === 'correct' ? previous.stability * 2.5 : TOY_INTERVAL_DAYS[body.grade];
  const stability = body.mode === 'learn' ? Math.min(growth, 1) : growth;
  const next: MockState = {
    dueAt: new Date(now + stability * DAY).toISOString(),
    reps: (previous?.reps ?? 0) + 1,
    lapses: (previous?.lapses ?? 0) + (body.grade === 'incorrect' ? 1 : 0),
    lastGrade: body.grade,
    lastReviewAt: new Date(now).toISOString(),
    stability,
  };
  states.set(k, next);
  return { edgeId: body.edgeId, scheduled: true, difficulty: 5, ...next };
}

/** Test hook: forget every mock review state. */
export function resetMockReviews(): void {
  states.clear();
  seeded = false;
}
